import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";
const ManagerList = () => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState("manager");
  const [expandedManager, setExpandedManager] = useState(null);

  useEffect(() => {
    const fetchEmployeesByRole = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = sessionStorage.getItem("token");
        let endpoint = "";

        switch (selectedRole) {
          case "manager":
            endpoint = "/all/managers";
            break;
          case "admin":
            endpoint = "/all/admin";
            break;
          case "storemanager":
            endpoint = "/all/storemanager";
            break;
          default:
            throw new Error("Invalid role selected");
        }

        const response = await fetch(`/api/employees${endpoint}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Something went wrong");
        }

        const data = await response.json();
        setManagers(data);
      } catch (error) {
        setError(error.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeesByRole();
  }, [selectedRole]);

  const fetchEmployeesByManager = async (managerID) => {
    try {
      const token = sessionStorage.getItem("token");

      const response = await fetch(
        `/api/employees/employees/${managerID}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch employees");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching employees by manager:", error);
      return [];
    }
  };

  const handleToggleManager = async (managerID) => {
    if (expandedManager?.managerID === managerID) {
      setExpandedManager(null);
    } else {
      const employees = await fetchEmployeesByManager(managerID);
      setExpandedManager({
        managerID,
        employees,
      });
    }
  };

  const roleLabels = {
    admin: "Admin",
    manager: "Manager",
    storemanager: "Store Manager",
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Employee List by Role"
        subtitle="View and manage employees across different roles"
        icon={<PeopleIcon />}
      />

      {/* Role Filter Tabs */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={selectedRole}
          exclusive
          onChange={(e, val) => {
            if (val !== null) setSelectedRole(val);
          }}
          sx={{
            bgcolor: "#f5f7fb",
            borderRadius: "10px",
            p: 0.5,
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: "8px !important",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              color: "#8b8fa3",
              px: 2.5,
              py: 0.75,
              "&.Mui-selected": {
                bgcolor: "#FFFFFF",
                color: "#667eea",
                fontWeight: 600,
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                "&:hover": {
                  bgcolor: "#FFFFFF",
                },
              },
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.5)",
              },
            },
          }}
        >
          <ToggleButton value="admin">Admin</ToggleButton>
          <ToggleButton value="manager">Manager</ToggleButton>
          <ToggleButton value="storemanager">Store Manager</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 10,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#667eea" }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid #FCA5A5",
            borderRadius: "16px",
            bgcolor: "#FEF2F2",
          }}
        >
          <Typography
            sx={{ color: "#DC2626", fontWeight: 500, fontSize: "0.875rem" }}
          >
            {error}
          </Typography>
        </Paper>
      )}

      {/* Empty State */}
      {!loading && !error && managers.length === 0 && (
        <EmptyState
          icon={<PeopleIcon />}
          title="No Employees Found"
          subtitle={`No employees found for the ${roleLabels[selectedRole]} role.`}
        />
      )}

      {/* Employee Table */}
      {!loading && !error && managers.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "2px solid #f0f0f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#1a1a2e" }}
            >
              {roleLabels[selectedRole]}s
            </Typography>
            <Chip
              label={`${managers.length} ${managers.length === 1 ? "employee" : "employees"}`}
              size="small"
              sx={{
                bgcolor: "#eef0ff",
                color: "#667eea",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 24,
              }}
            />
          </Box>

          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f7fb" }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#8b8fa3",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #f0f0f5",
                    }}
                  >
                    Employee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#8b8fa3",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #f0f0f5",
                    }}
                  >
                    Employee ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#8b8fa3",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #f0f0f5",
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    Contact
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#8b8fa3",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #f0f0f5",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Location
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#8b8fa3",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #f0f0f5",
                      display: { xs: "none", lg: "table-cell" },
                    }}
                  >
                    Joined
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#8b8fa3",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #f0f0f5",
                      display: { xs: "none", lg: "table-cell" },
                    }}
                  >
                    Work Mode
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#8b8fa3",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #f0f0f5",
                      width: 80,
                    }}
                  >
                    Team
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {managers.map((manager) => {
                  const isExpanded =
                    expandedManager?.managerID === manager.employeeid;
                  return (
                    <React.Fragment key={manager._id}>
                      <TableRow
                        sx={{
                          "&:hover": { bgcolor: "#f5f7fb" },
                          bgcolor: isExpanded ? "#f5f7fb" : "transparent",
                        }}
                      >
                        <TableCell
                          sx={{ borderBottom: "1px solid #f0f2f8", py: 2 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: "#eef0ff",
                                color: "#667eea",
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                              }}
                            >
                              {getInitials(manager.name)}
                            </Avatar>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.875rem",
                                  color: "#1a1a2e",
                                }}
                              >
                                {manager.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color: "#8b8fa3",
                                  display: { xs: "block", md: "none" },
                                }}
                              >
                                {manager.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #f0f2f8",
                            color: "#1a1a2e",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                          }}
                        >
                          {manager.employeeid}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #f0f2f8",
                            display: { xs: "none", md: "table-cell" },
                          }}
                        >
                          <Typography
                            sx={{ color: "#1a1a2e", fontSize: "0.8125rem" }}
                          >
                            {manager.email}
                          </Typography>
                          <Typography
                            sx={{ color: "#8b8fa3", fontSize: "0.75rem" }}
                          >
                            {manager.contactNumber}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #f0f2f8",
                            color: "#1a1a2e",
                            fontSize: "0.8125rem",
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          {manager.jobLocation}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #f0f2f8",
                            color: "#8b8fa3",
                            fontSize: "0.8125rem",
                            display: { xs: "none", lg: "table-cell" },
                          }}
                        >
                          {new Date(manager.dateOfJoining).toLocaleDateString("en-GB")}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #f0f2f8",
                            display: { xs: "none", lg: "table-cell" },
                          }}
                        >
                          <Chip
                            label={manager.workMode}
                            size="small"
                            sx={{
                              bgcolor:
                                manager.workMode?.toLowerCase() === "remote"
                                  ? "#F0FDF4"
                                  : manager.workMode?.toLowerCase() === "hybrid"
                                  ? "#FFFBEB"
                                  : "#f0f2f8",
                              color:
                                manager.workMode?.toLowerCase() === "remote"
                                  ? "#15803D"
                                  : manager.workMode?.toLowerCase() === "hybrid"
                                  ? "#B45309"
                                  : "#5a5f7a",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              height: 24,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ borderBottom: "1px solid #f0f2f8" }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleToggleManager(manager.employeeid)
                            }
                            sx={{
                              color: isExpanded ? "#667eea" : "#8b8fa3",
                              bgcolor: isExpanded ? "#EBF0FF" : "transparent",
                              "&:hover": { bgcolor: "#eef0ff" },
                            }}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Employee Sub-Table */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            sx={{
                              p: 0,
                              borderBottom: "2px solid #f0f0f5",
                              bgcolor: "#f5f7fb",
                            }}
                          >
                            <Box sx={{ px: 3, py: 2 }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.8125rem",
                                  color: "#1a1a2e",
                                  mb: 1.5,
                                }}
                              >
                                Employees under {manager.name}
                              </Typography>
                              {expandedManager.employees.length > 0 ? (
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                      xs: "1fr",
                                      sm: "repeat(2, 1fr)",
                                      md: "repeat(3, 1fr)",
                                    },
                                    gap: 1.5,
                                  }}
                                >
                                  {expandedManager.employees.map((employee) => (
                                    <Paper
                                      key={employee._id}
                                      elevation={0}
                                      sx={{
                                        border: "1px solid #eef0f6",
                                        borderRadius: "10px",
                                        p: 2,
                                        bgcolor: "#FFFFFF",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1.5,
                                          mb: 1.5,
                                        }}
                                      >
                                        <Avatar
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            bgcolor: "#f5f7fb",
                                            color: "#8b8fa3",
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          {getInitials(employee.name)}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.8125rem",
                                              color: "#1a1a2e",
                                              lineHeight: 1.3,
                                            }}
                                            noWrap
                                          >
                                            {employee.name}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: "0.6875rem",
                                              color: "#8b8fa3",
                                            }}
                                          >
                                            {employee.employeeid}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "0.75rem",
                                            color: "#8b8fa3",
                                          }}
                                          noWrap
                                        >
                                          {employee.email}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "0.75rem",
                                            color: "#8b8fa3",
                                          }}
                                        >
                                          {employee.contactNumber}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mt: 0.5,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "0.6875rem",
                                              color: "#8b8fa3",
                                            }}
                                          >
                                            {employee.jobLocation}
                                          </Typography>
                                          <Chip
                                            label={employee.workMode}
                                            size="small"
                                            sx={{
                                              height: 20,
                                              fontSize: "0.625rem",
                                              bgcolor: "#f5f7fb",
                                              color: "#5a5f7a",
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    </Paper>
                                  ))}
                                </Box>
                              ) : (
                                <Typography
                                  sx={{
                                    color: "#8b8fa3",
                                    fontSize: "0.8125rem",
                                    py: 2,
                                    textAlign: "center",
                                  }}
                                >
                                  No employees found under this manager.
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </PageContainer>
  );
};

export default ManagerList;
